import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import './OrganizerManagement.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface OrganizerApplication {
  id: string;
  userId: string;
  organizerName: string;
  email: string;
  emailVerified: boolean;
  phone: string;
  invoiceAddress: {
    street: string;
    postalCode: string;
    city: string;
    country: string;
  };
  applicantType: 'private' | 'business';
  orgNumber?: string;
  invoiceReference?: string;
  pricePerEvent?: number;
  pricePerEventSavedAt?: string;
  netDays?: number;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  reviewedAt?: string;
  rejectionReason?: string;
  user?: {
    email: string;
    name: string;
  };
}

const OrganizerManagement: React.FC = () => {
  const { accessToken } = useAuth();
  const [applications, setApplications] = useState<OrganizerApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'pending' | 'approved' | 'rejected' | 'all'>('all');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState<OrganizerApplication | null>(null);
  const [editPrice, setEditPrice] = useState('');
  const [editNetDays, setEditNetDays] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState<string | null>(null);
  const [systemSettings, setSystemSettings] = useState<any>(null);

  useEffect(() => {
    fetchApplications();
  }, [filter]);

  useEffect(() => {
    const fetchSystemSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/public/settings`);
        setSystemSettings(response.data);
      } catch (err) {
        console.error('Failed to fetch system settings:', err);
      }
    };
    fetchSystemSettings();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await axios.get(`${API_URL}/organizer-applications/list${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      setApplications(response.data.applications || []);
    } catch (err: any) {
      setError('Kunne ikke laste søknader');
      console.error('Error fetching applications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: string) => {
    try {
      setProcessingId(id);
      setShowConfirmModal(null);
      await axios.post(
        `${API_URL}/organizer-applications/${id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      await fetchApplications();
      setSuccessMessage('Søknad godkjent! Brukeren er nå arrangør.');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunne ikke godkjenne søknad');
      setTimeout(() => setError(''), 5000);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (id: string) => {
    try {
      setProcessingId(id);
      await axios.post(
        `${API_URL}/organizer-applications/${id}/reject`,
        { reason: rejectionReason },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      await fetchApplications();
      setShowRejectModal(null);
      setRejectionReason('');
      setSuccessMessage('Søknad avslått');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunne ikke avslå søknad');
      setTimeout(() => setError(''), 5000);
    } finally {
      setProcessingId(null);
    }
  };

  const handleOpenEdit = (app: OrganizerApplication) => {
    const defaultPrice = systemSettings?.organizerDefaults?.pricePerEvent || 1000;
    const defaultNetDays = systemSettings?.organizerDefaults?.netDays || 14;
    setShowEditModal(app);
    setEditPrice(app.pricePerEvent?.toString() || defaultPrice.toString());
    setEditNetDays(app.netDays?.toString() || defaultNetDays.toString());
  };

  const handleSaveEdit = async () => {
    if (!showEditModal) return;

    try {
      setProcessingId(showEditModal.id);
      const priceValue = editPrice ? parseFloat(editPrice) : undefined;
      const netDaysValue = editNetDays ? parseInt(editNetDays) : undefined;
      
      await axios.patch(
        `${API_URL}/organizer-applications/${showEditModal.id}`,
        { pricePerEvent: priceValue, netDays: netDaysValue },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      
      await fetchApplications();
      setShowEditModal(null);
      setEditPrice('');
      setEditNetDays('');
      setSuccessMessage('Søknad oppdatert');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunne ikke oppdatere søknad');
      setTimeout(() => setError(''), 5000);
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'approved': return '#10B981';
      case 'pending': return '#F59E0B';
      case 'rejected': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'approved': return 'Godkjent';
      case 'pending': return 'Ventende';
      case 'rejected': return 'Avslått';
      default: return status;
    }
  };

  if (loading) {
    return <div className="loading">Laster søknader...</div>;
  }

  return (
    <div className="organizer-management">
      <div className="management-header">
        <h2>Arrangøradministrasjon</h2>
      </div>

      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      {error && <div className="error-message">{error}</div>}

      <div className="filter-tabs">
        <button
          className={filter === 'all' ? 'active' : ''}
          onClick={() => setFilter('all')}
        >
          Alle ({applications.length})
        </button>
        <button
          className={filter === 'pending' ? 'active' : ''}
          onClick={() => setFilter('pending')}
        >
          Ventende ({applications.filter(a => a.status === 'pending').length})
        </button>
        <button
          className={filter === 'approved' ? 'active' : ''}
          onClick={() => setFilter('approved')}
        >
          Godkjente ({applications.filter(a => a.status === 'approved').length})
        </button>
        <button
          className={filter === 'rejected' ? 'active' : ''}
          onClick={() => setFilter('rejected')}
        >
          Avslåtte ({applications.filter(a => a.status === 'rejected').length})
        </button>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Arrangør</th>
              <th>Bruker</th>
              <th>E-post</th>
              <th>Telefon</th>
              <th>Type</th>
              <th>Pris</th>
              <th>Netto dager</th>
              <th>Opprettet</th>
              <th>Handling</th>
            </tr>
          </thead>
          <tbody>
            {applications.length === 0 ? (
              <tr>
                <td colSpan={10} className="no-results">
                  Ingen søknader funnet
                </td>
              </tr>
            ) : (
              applications.map((app) => (
                <tr key={app.id}>
                  <td>
                    <span 
                      className="role-badge" 
                      style={{ backgroundColor: getStatusBadgeColor(app.status) }}
                    >
                      {getStatusLabel(app.status)}
                    </span>
                  </td>
                  <td className="user-name">{app.organizerName}</td>
                  <td>{app.user?.name || 'Ukjent'}</td>
                  <td className="user-email">
                    {app.email}
                    {app.emailVerified ? (
                      <span className="verified-badge" style={{ marginLeft: '8px' }}>✓</span>
                    ) : (
                      <span className="unverified-badge" style={{ marginLeft: '8px' }}>✗</span>
                    )}
                  </td>
                  <td>{app.phone}</td>
                  <td>{app.applicantType === 'private' ? 'Privat' : 'Virksomhet'}</td>
                  <td>
                    {app.pricePerEvent ? `${app.pricePerEvent.toLocaleString('nb-NO')} kr` : '-'}
                  </td>
                  <td>
                    {app.netDays || '-'}
                  </td>
                  <td className="created-date">
                    {new Date(app.createdAt).toLocaleDateString('nb-NO')}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {app.status === 'pending' && (
                        <>
                          <button
                            onClick={() => setShowConfirmModal(app.id)}
                            disabled={!app.emailVerified || processingId === app.id}
                            className="btn btn-small btn-success"
                            title={!app.emailVerified ? 'E-post må være verifisert' : ''}
                          >
                            Godkjenn
                          </button>
                          <button
                            onClick={() => setShowRejectModal(app.id)}
                            disabled={processingId === app.id}
                            className="btn btn-small btn-danger"
                          >
                            Avslå
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleOpenEdit(app)}
                        className={`btn btn-small ${!app.pricePerEvent || !app.netDays ? 'btn-danger' : 'btn-secondary'}`}
                      >
                        Rediger
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay" onClick={() => setShowConfirmModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Godkjenn søknad</h3>
            <p>Er du sikker på at du vil godkjenne denne søknaden? Brukeren vil bli oppgradert til arrangør.</p>
            <div className="modal-actions">
              <button onClick={() => setShowConfirmModal(null)} className="btn btn-secondary">
                Avbryt
              </button>
              <button
                onClick={() => handleApprove(showConfirmModal)}
                disabled={processingId === showConfirmModal}
                className="btn btn-success"
              >
                {processingId === showConfirmModal ? 'Godkjenner...' : 'Godkjenn søknad'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Rediger søknad</h3>
            
            <div style={{ marginBottom: '20px' }}>
              <p><strong>Arrangør:</strong> {showEditModal.organizerName}</p>
              <p><strong>E-post:</strong> {showEditModal.email}</p>
              <p><strong>Status:</strong> {getStatusLabel(showEditModal.status)}</p>
            </div>

            <div className="form-group">
              <label htmlFor="pricePerEvent">Pris per arrangement (NOK)</label>
              <input
                type="number"
                id="pricePerEvent"
                value={editPrice}
                onChange={(e) => setEditPrice(e.target.value)}
                placeholder="F.eks. 1000"
                min="0"
                step="100"
              />
              <p className="form-help">Standard: 1000 kr</p>
            </div>

            <div className="form-group">
              <label htmlFor="netDays">Netto per antall dager</label>
              <input
                type="number"
                id="netDays"
                value={editNetDays}
                onChange={(e) => setEditNetDays(e.target.value)}
                placeholder="14"
                min="1"
                step="1"
              />
              <p className="form-help">Betalingsfrist i dager. Standard: 14 dager</p>
            </div>

            <div className="modal-actions">
              <button onClick={() => setShowEditModal(null)} className="btn btn-secondary">
                Avbryt
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={processingId === showEditModal.id}
                className="btn btn-primary"
              >
                {processingId === showEditModal.id ? 'Lagrer...' : 'Lagre endringer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Avslå søknad</h3>
            <p>Vennligst oppgi en grunn for avslaget:</p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Begrunnelse..."
              rows={4}
            />
            <div className="modal-actions">
              <button onClick={() => setShowRejectModal(null)} className="btn btn-secondary">
                Avbryt
              </button>
              <button
                onClick={() => handleReject(showRejectModal)}
                disabled={!rejectionReason.trim()}
                className="btn btn-danger"
              >
                Bekreft avslag
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizerManagement;
